<script setup lang="ts">
type ActiveButton = "cart" | "review-and-confirm" | "checkout";

const route = useRoute();
const localePath = useLocalePath();
const { checkGiftCardCodeSchema } = useGiftCard();
const client = useClient();

const { t } = useI18n();
const { isLoggedIn } = useAuth();
const globalStore = useGlobalStore();
const { isLoading, progress, isClientBlocked } = storeToRefs(globalStore);
const { payOrder } = useProducts();
const { navLoading, runWithLoading } = useFooterNav();

const {
  selectedLocation,
  selectedPaymentMethod,
  tapLoading,
  isNavigating,
  isCardValid,
  cartTotal,
  cartCount,
  goToOrderSummary,
  orderSummaryDetails,
  payOrderLoading,
  finalTotalAfterCoupon,
  goToOrderCheckout,
  invoice,
} = storeToRefs(useBusinessStore());

const activeButton = computed((): ActiveButton | undefined => {
  if ((route.meta.keys as string[])?.includes("cart")) return "cart";
  if ((route.meta.keys as string[])?.includes("summary"))
    return "review-and-confirm";
  if ((route.meta.keys as string[])?.includes("checkout")) return "checkout";
  return undefined;
});

const completeProfileToContinue = async () => {
  goToOrderSummary.value = true;
  await navigateTo(localePath(`/complete`));
};

const loginToContinue = () => {
  goToOrderSummary.value = true;
  globalStore.openSignInModal({
    onSuccess: (c) => {
      if (c?.profile_completed && c?.mobile) {
        runWithLoading(handleConfirm);
      }
    },
  });
};

const submitPayment = async () => {
  if (selectedPaymentMethod.value?.key === "tap-cc") {
    const windowCardSDK = ref(window.CardSDK);
    const { tokenize } = windowCardSDK.value;
    tapLoading.value = true;
    await tokenize();
    return;
  } else if (selectedPaymentMethod.value?.key === "gift-card") {
    const { errors } = await checkGiftCardCodeSchema();
    if (errors) {
      return;
    }
  }

  await payOrder();
};

const displayTotal = computed(() => {
  const summaryTotal = orderSummaryDetails.value?.grand_total;
  const isSummaryStep =
    activeButton.value === "review-and-confirm" ||
    activeButton.value === "checkout";

  if (isSummaryStep && summaryTotal !== undefined && summaryTotal !== null) {
    return useFormatAmount(summaryTotal);
  }

  return useFormatAmount(finalTotalAfterCoupon.value || cartTotal.value);
});

const handleConfirm = async () => {
  if (orderSummaryDetails.value?.grand_total === "0.000") {
    await submitPayment();
    return;
  }
  goToOrderCheckout.value = true;
  await navigateTo(
    localePath(`/${selectedLocation.value?.identifier}/cart/checkout`)
  );
};
</script>

<template>
  <CommonFooterShell
    :label1="t('cart_count')"
    :value1="cartCount"
    :label2="t('total')"
    :value2="$formatPrice(displayTotal)"
  >
    <template #action>
      <template v-if="activeButton === 'cart'">
        <template v-if="isLoggedIn">
          <UButton
            v-if="client?.profile_completed"
            :label="t('label.continue')"
            :color="`${selectedLocation?.theme_name}-secondary`"
            :loading="navLoading"
            :disabled="!(cartCount > 0) || isLoading || progress"
            size="xl"
            class="min-w-26 xs:min-w-28 rounded-xl py-3 flex items-center justify-center"
            @click="
              runWithLoading(() =>
                navigateTo(
                  localePath(
                    `/${selectedLocation?.identifier}/cart/review-and-confirm`
                  )
                )
              )
            "
          />
          <UButton
            v-else
            :disabled="!(cartCount > 0) || isLoading || progress"
            :loading="navLoading"
            :label="t('label.complete_profile')"
            :color="`${selectedLocation?.theme_name}-secondary`"
            size="xl"
            class="min-w-26 xs:min-w-28 rounded-xl py-3 flex items-center justify-center truncate"
            @click="runWithLoading(completeProfileToContinue)"
          />
        </template>
        <UButton
          v-else
          :label="$t('label.sign_in')"
          :disabled="!(cartCount > 0) || isLoading || progress"
          :loading="navLoading"
          :color="`${selectedLocation?.theme_name}-secondary`"
          size="xl"
          class="min-w-26 xs:min-w-28 rounded-xl py-3 flex items-center justify-center"
          @click="runWithLoading(loginToContinue)"
        />
      </template>

      <UButton
        v-else-if="activeButton === 'review-and-confirm'"
        :label="t('label.confirm')"
        :color="`${selectedLocation?.theme_name}-secondary`"
        :disabled="!(cartCount > 0) || isLoading || progress || isClientBlocked"
        :loading="navLoading || payOrderLoading"
        size="xl"
        class="min-w-26 xs:min-w-28 rounded-xl py-3 text-center flex items-center justify-center"
        @click="runWithLoading(handleConfirm)"
      />

      <UButton
        v-else-if="activeButton === 'checkout'"
        :label="$t('label.checkout')"
        :loading="payOrderLoading || tapLoading"
        :color="`${selectedLocation?.theme_name}-secondary`"
        :disabled="
          !selectedPaymentMethod?.id ||
          (!isCardValid && selectedPaymentMethod?.key === 'tap-cc') ||
          (!!selectedPaymentMethod?.accept_invoice && !invoice) ||
          isNavigating
        "
        size="xl"
        class="min-w-26 xs:min-w-28 rounded-xl py-3 flex items-center justify-center"
        @click="submitPayment"
      />
    </template>
  </CommonFooterShell>
</template>
